import { parkingWords, type ParkingHours } from './parking-hours'
import { parkingRules, rangesHit } from './parking-rules'
import { shownTime } from './round-config'
import { el } from './ui'

// Asks before a visit goes into a customer's parking hours (Liam, 22 Oct 2026: "a suggestion comes up when we try and
// drag it into that time or book them into that time. Because although we could override it, we really would want to
// know"; docs/plans/job-bits/PLAN.md 1, with Sonnet's and GPT-6.1's edge cases). Part of Parking notes, off as standard.
// The sheet quotes their note as written, so what Roundly read from it is never stated as fact on its own, and says the
// hours it read beside it. Nothing is stored about the answer.

export interface ParkingCustomer { customer_name?: string | null; parking?: string | null; duration_minutes?: number | null }

// The hours this visit would sit in, or null (Parking notes off, no time, nothing read, not in them).
// Always on now (parking design B, 27 Oct 2026): any customer with parking hours, on that day of the week, start to finish
// (src/parking-rules.ts). With no day given, every day's hours count.
export function parkingClash(c: ParkingCustomer, slot: string | null | undefined, date: string | null = null): ParkingHours | null {
  if (!slot) return null
  return rangesHit(parkingRules(c.parking), date, slot, c.duration_minutes ?? null)[0] ?? null
}

// The words under a time in Book a visit: their note, quoted, and the hours read.
export function parkingWarning(c: ParkingCustomer, hours: ParkingHours): string {
  return `Their parking note says “${(c.parking ?? '').trim()}” (${parkingWords(hours)}).`
}

// The Diary's question before a move, a pencilled time or a set time lands in the hours. True when it asked (the caller
// stops; onYes carries on with the change as it was).
export function askParking(c: ParkingCustomer, slot: string | null | undefined, onYes: () => void, date: string | null = null): boolean {
  const hours = parkingClash(c, slot, date)
  if (!hours || !slot) return false
  const name = (c.customer_name ?? '').trim() || 'them'
  const first = name.split(/\s+/)[0]!
  const dialog = el('dialog', { class: 'booking-dialog they-replied parking-ask', 'aria-label': `Parking at ${name}'s` }) as HTMLDialogElement
  const shut = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const body = el('div', { class: 'they-replied-body' })
  const close = (): void => { if (dialog.open) dialog.close() }
  shut.addEventListener('click', close)
  dialog.addEventListener('close', () => dialog.remove())
  const choice = (words: string, small: string, main = false): HTMLButtonElement =>
    el('button', { type: 'button', class: `they-replied-choice${main ? ' is-main' : ''}` }, el('strong', {}, words), el('small', {}, small)) as HTMLButtonElement
  const other = choice('Pick another time', 'Nothing changes.', true)
  other.addEventListener('click', close)
  const anyway = choice(`Put ${first} at ${shownTime(slot)} anyway`, `Their note's hours are ${parkingWords(hours)}.`)
  anyway.addEventListener('click', () => { close(); onYes() })
  body.append(el('p', { class: 'they-replied-about' }, parkingWarning(c, hours)), other, anyway)
  dialog.append(el('header', { class: 'booking-head' }, el('h2', {}, `Parking at ${name}'s`), shut), body)
  document.body.append(dialog)
  dialog.showModal()
  other.focus({ preventScroll: true })
  return true
}
